namespace Solovis.RpcClients {

  export interface IRpcClient {
    /**
     * Invoke POST-RPC method asynchronously on server.
     * @param url
     * @param requestMessage
     * @param callback
     */
    invoke(
      url: string,
      requestMessage: any,
      callback?: (response: any, success?: boolean) => void,
      requestOptions?: any
    ): Promise<any>;

    /**
     * Invoke POST-RPC method asynchronously on server and show waiting dialog while doing so. 
     * @param url
     * @param requestMessage
     * @param callback
     * @param suppressDefaultErrorMessage
     */
    invokeAndWait(
      url: string,
      requestMessage: any,
      callback?: (response: any, success?: boolean) => void,
      suppressDefaultErrorMessage?: boolean
    ): Promise<any>
  }


  export class RpcClient implements IRpcClient {

    static forceNoReadOnly : boolean = false;
    static forceNoReadOnlyTimer : NodeJS.Timeout;

    constructor() {
    }

    public pollForJobResult(jobId: string, callback: (response: any, success?: boolean) => void, callbackContext?: any) {
      var me = this;

      var maxPollCount = 452, // 60 minutes
        currentPollCount = 1;

      function invokeCallback(response, success) {
        if (callbackContext) {
          callback.call(callbackContext, response, success);
        } else {
          callback(response, success);
        }
      }

      function pollForResult(jobId) {
        Solovis.Rpc.invoke(me._pollJobStatusEndpointWithArea, { jobId: jobId }, function (response) {
          if (response && response.result && response.result.status === "Completed") {
            Solovis.Rpc.invoke("/Rpc/QueuedJob/GetJobData", { jobId: jobId }, function (response) {
              if (response.error != null) {
                invokeCallback(response, false);
              } else {
                invokeCallback(response, true);
              }
            }).catch((err) => { console.error(err); });
          } else if (response && response.result && response.result.status === "Failed") {
            invokeCallback(response, true);
          } else {
            if (currentPollCount >= maxPollCount) {
              response.error = {
                originalRequest: {
                  jobId: jobId
                }
              };
              invokeCallback(response, true);
            } else {
              var pollInterval = 250; //ms
              if (currentPollCount > 422) {
                pollInterval = 60000; //ms
              }
              else if (currentPollCount > 74) {
                pollInterval = 5000; //ms
              }
              else if (currentPollCount > 24) {
                pollInterval = 1000; //ms
              }
              else if (currentPollCount > 8) {
                pollInterval = 500; //ms
              }
              setTimeout(function () { pollForResult(jobId); }, pollInterval);
              currentPollCount++;
            }
          }
        });
      }
      pollForResult(jobId);
    }

    public shouldPollForResult(data, url, XMLHttpRequest) {
      return data && data.result && data.result.jobId // jobId is present in server response
        && url !== this._pollJobStatusEndpointWithArea // we're not already in a success handler for a poll request (to avoid infinite loop)
        && XMLHttpRequest.getResponseHeader('location').indexOf(this._pollJobStatusEndpoint) > -1; // location header is set to the polling endpoint
    }

    public invoke(
      url: string,
      requestMessage: any,
      callback?: (response: any, success?: boolean) => void,
      requestOptions?: any
    ): Promise<any> {

      var me = this;
      var promise = new Promise(function (resolve, reject) {
        url = Solovis.resolveUrl(url);

        var callbackWrapper = function (response, success) {
          if (!success) {
            if (!response) {
              response = {};
            }
            if (!response.error) {
              response.error = {};
            }
            response.error.originalRequest = {
              url: url,
              requestMessage: requestMessage
            };
          }

          if (callback) {
            callback(response, success);
          }

          if (success) {
            resolve(response);
          }
          else {
            reject(response);
          }
        };

        jQuery.ajax({
          url: url,
          type: "POST",
          contentType: "x-post-rpc/json",
          data: JSON.stringify(requestMessage),
          dataType: "json",
          processData: false,

          success: function (data, textStatus, XMLHttpRequest) {
            if (data != null) {
              if (data.error != null) {
                callbackWrapper(data, false);
              }
              else {
                if (me.shouldPollForResult(data, url, XMLHttpRequest)) {
                  me.pollForJobResult(data.result.jobId, callbackWrapper);
                } else {
                  callbackWrapper(data, true);
                }
              }
            }
            else {
              if (XMLHttpRequest.status == 0) {
                var response = {
                  error: {
                    message: "Request was canceled.",
                    canceled: true
                  }
                };

                callbackWrapper(response, false);
              }
              else {
                callbackWrapper(null, true);
              }
            }
          },

          error: function (XMLHttpRequest, textStatus, errorThrown) {
            var response = {
              error: {
                message: "Request failed.",
                http: {
                  status: XMLHttpRequest.status,
                  responseText: XMLHttpRequest.responseText,
                  contentType: XMLHttpRequest.getResponseHeader("Content-Type")
                }
              }
            };

            callbackWrapper(response, false);
          },

          beforeSend: function (xhr) {
            xhr.setRequestHeader("X-POST-RPC", "true");
            xhr.setRequestHeader("Enable-Read-Only", "true");
            if ((Solovis as any).Info && (Solovis as any).Info.csrfToken) {
              xhr.setRequestHeader("X-POST-RPC-CSRF", (Solovis as any).Info.csrfToken);
            }
            if (RpcClient.forceNoReadOnly || requestOptions?.forceNoReadOnly) {
              xhr.setRequestHeader("Force-No-Read-Only", "true");
            }
          }
        });
      });

      return promise;
    };

    public invokeAndWait(
      url: string,
      requestMessage: any,
      callback?: (response: any, success?: boolean) => void,
      suppressDefaultErrorMessage?: boolean
    ): Promise<any> {

      this.showWaitingDialog();

      var promise = this.invoke(url, requestMessage,
        (response, success) => {
          this.hideWaitingDialog();

          if (success) {
            if (callback) {
              callback(response, success);
            }
          }
          else {
            if (!suppressDefaultErrorMessage) {
              this.showErrorDialog(response.error);
            }

            if (callback) {
              callback(response, success);
            }
          }
        });

      return promise;
    };

    private _errorDialogElement = null;

    public showErrorDialog(errorObject) {
      var _errorDialogElement = $("<div>");

      if (!errorObject.http) {
        _errorDialogElement.append(document.createTextNode(errorObject.message));
      }
      else {
        var iframe = document.createElement("iframe");
        iframe.setAttribute("src", "about:blank");
        iframe.setAttribute("frameborder", "0");
        iframe.setAttribute("marginwidth", "0");
        iframe.setAttribute("marginheight", "0");
        iframe.style.width = '100%';
        iframe.style.height = '95%';
        iframe.style.margin = '0px';
        iframe.style.padding = '0px';
        iframe.onload = function () {
          iframe.contentDocument.body.innerHTML = errorObject.http.responseText;
        };
        _errorDialogElement.append(iframe);
      }

      var errorDialog = new Solovis.UI.Dialog({
        container: _errorDialogElement,
        modal: true,
        autoOpen: true,
        title: "Error",
        center: true,
        width: 500,
        height: 300,
        maxWidth: 750,
        maxHeight: 600,
        destroyOnClose: true,
        actions: ["Close"]
      });
    }

    private _pollJobStatusEndpointWithArea = "/Rpc/QueuedJob/GetJobStatus";
    private _pollJobStatusEndpoint = "/QueuedJob/GetJobStatus";

    private _waitDialogInfo = null;

    public showWaitingDialog() {
      // if (Solovis.UX.Utils.isNewUxEnabled()) {
      //     Solovis.UX.Utils.showLoader();
      //     return     
      // }

      if (!this._waitDialogInfo) {
        this._waitDialogInfo = {};

        var waitDialogElement = $("<div>")
          .css("position", "fixed")
          .css("top", "0").css("left", "0")
          .css("width", "100%").css("height", "100%")
          .css("display", "none")
          .css("pointer-events", "none");

        var container = $("<div style='position: relative; width: 100%; height: 100%;'></div>").appendTo(waitDialogElement);
        var backgroundDiv = $("<div style='position: fixed; width: 100%; height: 100%; background-color: #000; opacity: 0.6'></div>").appendTo(container);

        var waitImageDiv = Solovis.UI.StockAnimations.createSpinningCircles(
          {
            diameter: "100px",
            top: "50%",
            left: "50%",
            showShadow: true
          })
          .appendTo(container);

        this._waitDialogInfo.waitDialogElement = waitDialogElement;
        this._waitDialogInfo.waitImageDiv = waitImageDiv;
        $(document.body).append(this._waitDialogInfo.waitDialogElement);

        var zIndexToUse = Solovis.UI.DomHelper.findMaxZIndexForElementChildren(document.body);
        zIndexToUse = zIndexToUse + 5;

        this._waitDialogInfo.waitDialogElement.css("z-index", zIndexToUse);
        this._waitDialogInfo.waitDialogElement.css("display", "block");
      }
    }

    public hideWaitingDialog() {
      // if (Solovis.UX.Utils.isNewUxEnabled()) {
      //     Solovis.UX.Utils.hideLoader();
      //     return     
      // }

      if (this._waitDialogInfo) {
        this._waitDialogInfo.waitDialogElement.remove();
        this._waitDialogInfo = null;
      }
    }

    public noReadOnly() { RpcClient.noReadOnly(); }

    public static noReadOnly() {
      function setFlag() {
        RpcClient.forceNoReadOnly = false;
      }

      if (RpcClient.forceNoReadOnlyTimer) {
        clearTimeout(RpcClient.forceNoReadOnlyTimer);
      }

      RpcClient.forceNoReadOnly = true;
      RpcClient.forceNoReadOnlyTimer = setTimeout(setFlag, 5000);
    }
  }
}

namespace Solovis {

  export const Rpc: RpcClients.RpcClient = new RpcClients.RpcClient();

}